import { useState, useEffect } from "react";

type ComponentStatus = "operational" | "degraded" | "outage";

interface ComponentHealth {
  name: string;
  status: ComponentStatus;
  description: string;
  latency_ms?: number;
}

interface StatusResponse {
  status: ComponentStatus;
  timestamp: string;
  components: ComponentHealth[];
}

const API_URL = import.meta.env.VITE_API_URL || "https://api.verkh.io";
const APP_URL = import.meta.env.VITE_APP_URL || "https://app.verkh.io";

const statusConfig = {
  operational: {
    color: "text-green-500",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    dot: "bg-green-500",
    label: "Operational",
    headerBg: "from-green-500/20 to-green-600/10",
    headerText: "All Systems Operational",
  },
  degraded: {
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    dot: "bg-amber-500",
    label: "Degraded",
    headerBg: "from-amber-500/20 to-amber-600/10",
    headerText: "Partial System Outage",
  },
  outage: {
    color: "text-red-500",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    dot: "bg-red-500",
    label: "Outage",
    headerBg: "from-red-500/20 to-red-600/10",
    headerText: "Major System Outage",
  },
};

// Simple icon components
function MountainIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function getStatusIcon(status: ComponentStatus, className?: string) {
  switch (status) {
    case "operational":
      return <CheckCircleIcon className={className} />;
    case "degraded":
      return <AlertTriangleIcon className={className} />;
    case "outage":
      return <XCircleIcon className={className} />;
  }
}

export default function App() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  async function fetchStatus() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/health/status`);
      if (!response.ok) {
        throw new Error("Failed to fetch status");
      }
      const data = await response.json();
      setStatus(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load status");
      setStatus({
        status: "outage",
        timestamp: new Date().toISOString(),
        components: [
          {
            name: "API",
            status: "outage",
            description: "Unable to reach API",
          },
        ],
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const overallConfig = status
    ? statusConfig[status.status]
    : statusConfig.operational;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-between max-w-4xl mx-auto">
        <a href={APP_URL} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
            <MountainIcon className="w-6 h-6 text-slate-900" />
          </div>
          <span className="text-xl font-bold text-white">Verkh Status</span>
        </a>
        <a
          href={`${APP_URL}/login`}
          className="px-4 py-2 text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded-lg transition-colors text-sm"
        >
          Go to App
        </a>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-8 py-8">
        {/* Overall Status Banner */}
        <div
          className={`rounded-2xl p-8 mb-8 bg-gradient-to-r ${overallConfig.headerBg} border ${overallConfig.border}`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-full ${overallConfig.bg} flex items-center justify-center`}
            >
              {getStatusIcon(
                status?.status || "operational",
                `w-6 h-6 ${overallConfig.color}`
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">
                {overallConfig.headerText}
              </h1>
              <p className="text-white/60 text-sm mt-1">
                {lastUpdated
                  ? `Last checked: ${lastUpdated.toLocaleTimeString()}`
                  : "Checking status..."}
              </p>
            </div>
            <button
              onClick={fetchStatus}
              disabled={loading}
              className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
              title="Refresh status"
            >
              <RefreshIcon
                className={`w-5 h-5 text-white/60 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-xl p-4 mb-6 bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm">
              Unable to connect to API. Showing last known status.
            </p>
          </div>
        )}

        {/* Components List */}
        <div className="mb-12">
          <h2 className="text-lg font-semibold text-white mb-4">
            System Components
          </h2>
          <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10">
            {status?.components.map((component) => {
              const config = statusConfig[component.status];

              return (
                <div
                  key={component.name}
                  className="p-5 bg-white/5 flex items-center justify-between hover:bg-white/[0.07] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-3 h-3 rounded-full ${config.dot} ${
                        component.status === "operational" ? "" : "animate-pulse"
                      }`}
                    />
                    <div>
                      <h3 className="font-medium text-white">
                        {component.name}
                      </h3>
                      <p className="text-sm text-white/50">
                        {component.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {component.latency_ms !== undefined && (
                      <span className="text-xs text-white/40 tabular-nums">
                        {component.latency_ms}ms
                      </span>
                    )}
                    <span
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}
                    >
                      {config.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Uptime and incident history are deliberately absent.
            Until 2026-09 this page published "99.9%" overall uptime, ninety
            green day-squares, and "All systems have been operational for the
            past 90 days". None of the three was measured: the percentage was
            a literal, the squares were `Array.from({ length: 90 })` with a
            fixed green class, and the incident panel was static markup.

            Only the component list above reflects reality — it comes from
            `GET /health/status`, which probes each dependency on request.
            That endpoint has no history behind it, so there is nothing to
            compute an uptime figure or an incident list from.

            Publishing an unmeasured availability number on a public status
            page is a claim to customers, and getting it wrong in the
            reassuring direction is the expensive way to be wrong: the page is
            most likely to be read during an outage, which is exactly when a
            hardcoded green reads as a lie. An honest gap is better than a
            confident fabrication, so the sections are gone rather than
            softened.

            Restoring them needs a stored history of the probe results — see
            finding 59 in docs/codebase/01-findings-register.md. Guarded by
            packages/status/src/App.honesty.test.ts, which fails if a
            hardcoded availability claim reappears. */}

        {/* Subscribe Section */}
        <div className="rounded-2xl border border-white/10 p-6 bg-white/5 text-center">
          <h3 className="text-white font-medium mb-2">Stay Updated</h3>
          <p className="text-white/50 text-sm mb-4">
            Get notified when there's a service disruption
          </p>
          <a
            href="mailto:support@verkh.io?subject=Subscribe%20to%20Status%20Updates"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-medium text-sm transition-colors"
          >
            Subscribe to Updates
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-8 text-center border-t border-white/10 mt-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-white/40 text-sm">
            Status updates every 60 seconds.{" "}
            <a
              href="mailto:support@verkh.io"
              className="text-amber-400 hover:text-amber-300"
            >
              Report an issue
            </a>
          </p>
          <p className="text-white/30 text-xs mt-2">
            &copy; {new Date().getFullYear()} Verkh. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
